import { CobaltButton } from '@cae/cobalt-react/components/button';
import { CobaltIconChevronLeftOutline } from '@cae/cobalt-react/icons/outline/CobaltIconChevronLeftOutline';
import { CobaltIconChevronRightOutline } from '@cae/cobalt-react/icons/outline/CobaltIconChevronRightOutline';
import * as React from 'react';

import type {
  TilesCarouselProps,
  TilesCarouselScrollDirection,
} from './TilesCarousel.types';

import styles from './TilesCarousel.module.css';

const TilesCarousel = React.memo(
  ({
    children,
    scrollContainerClassName,
  }: Readonly<TilesCarouselProps>): React.ReactElement => {
    const scrollRef = React.useRef<HTMLDivElement>(null);
    const [showLeft, setShowLeft] = React.useState<boolean>(false);
    const [showRight, setShowRight] = React.useState<boolean>(true);

    const scroll = React.useCallback(
      (direction: TilesCarouselScrollDirection): void => {
        const el = scrollRef.current;

        if (!el) return;
        el.scrollBy({
          left:
            direction === 'right'
              ? el.clientWidth * 0.8
              : -(el.clientWidth * 0.8),
          behavior: 'smooth',
        });
      },
      []
    );

    React.useEffect(() => {
      const el = scrollRef.current;
      if (!el) return;

      const updateArrows = (): void => {
        setShowLeft(el.scrollLeft > 0);
        setShowRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
      };

      el.addEventListener('scroll', updateArrows);
      const rafId = requestAnimationFrame(updateArrows);

      const resizeObserver = new ResizeObserver(updateArrows);
      resizeObserver.observe(el);

      return () => {
        el.removeEventListener('scroll', updateArrows);
        resizeObserver.disconnect();
        cancelAnimationFrame(rafId);
      };
    }, [children]);

    return (
      <div className={styles.tilesCarousel} data-testid="tiles-carousel">
        {showLeft && (
          <div
            className={`${styles.tilesCarouselFade} ${styles.tilesCarouselFadeLeft}`}
          >
            <CobaltButton
              variant="ghost"
              size="small"
              className={styles.tilesCarouselButton}
              onClick={() => scroll('left')}
              aria-label="Scroll left"
            >
              <CobaltIconChevronLeftOutline aria-hidden="true" />
            </CobaltButton>
          </div>
        )}
        <div
          ref={scrollRef}
          className={`${styles.tilesCarouselScroll} ${scrollContainerClassName ?? ''}`}
        >
          {children}
        </div>
        {showRight && (
          <div
            className={`${styles.tilesCarouselFade} ${styles.tilesCarouselFadeRight}`}
          >
            <CobaltButton
              variant="ghost"
              size="small"
              className={styles.tilesCarouselButton}
              onClick={() => scroll('right')}
              aria-label="Scroll right"
            >
              <CobaltIconChevronRightOutline aria-hidden="true" />
            </CobaltButton>
          </div>
        )}
      </div>
    );
  }
);

TilesCarousel.displayName = 'TilesCarousel';

export { TilesCarousel };
